import React, { useMemo } from 'react';
import styled from 'styled-components';
import { SortableElement } from 'react-sortable-hoc';

import { BakedBreadEntity } from 'src/firebase/db/BakedBreads';
import { ShopEntity } from 'src/firebase/db/Shops';
import { formatDate } from 'src/helpers/UtilityHelper';

import StyleConfig from 'src/constants/StyleConfig';
import { LinkButton } from 'src/components/atoms/button/Button';

type Props = {
  item: BakedBreadEntity;
  shop: ShopEntity;
  query: string;
  listType: string;
  bakedBreadTag: string;
};

const BakedBreadListItem = SortableElement((props: Props) => {
  const {
    item: { id, image, name: itemName, is_stopped, stats, updated_at, baked_bread_tags },
    shop: { name: shopName },
    query,
    bakedBreadTag,
    listType,
  } = props;

  const breadName = useMemo(() => `${itemName} ${is_stopped ? '（販売停止中）' : ''}`, [
    itemName,
    is_stopped,
  ]);

  const userCount = useMemo(() => (stats ? stats.userCount : 0), [stats]);

  const updatedAt = useMemo(() => formatDate(updated_at, 'YYYY年MM月DD日(ddd) HH:mm'), [
    updated_at,
  ]);

  const isDisplayed = useMemo(() => {
    const existsQuery = query !== '';
    const existTag = bakedBreadTag !== '';
    if (!existsQuery && !existTag) {
      return true;
    }
    const hasQuery = itemName.indexOf(query) !== -1;
    const hasTag =
      baked_bread_tags && baked_bread_tags[0]
        ? baked_bread_tags.some((v) => v.id === bakedBreadTag)
        : false;
    if (existsQuery && !existTag) {
      return hasQuery;
    } else if (!existsQuery && existTag) {
      return hasTag;
    } else {
      return hasQuery && hasTag;
    }
  }, [query, itemName, bakedBreadTag, baked_bread_tags]);

  const isDetail = useMemo(() => listType === 'detail', [listType]);

  if (!isDisplayed) {
    return <Container isDetail={false} />;
  }

  return (
    <Container isDetail={isDetail}>
      {isDetail ? (
        <Detail>
          <div className="bread">
            <img className="bread-thumbnail" src={image} alt="" role="presentation" />
            <div className="bread-description">
              <h3 className="bread-name">{breadName}</h3>
              <div className="bread-shop">{shopName}</div>
              <div className="bread-count">
                現在の焼き上がり通知登録人数：
                <span>{userCount}</span>
              </div>
              <div className="bread-timestamp">
                更新日：
                <span>{updatedAt}</span>
              </div>
            </div>
          </div>
          <div className="action">
            <LinkButton isDisabled={false} url={`/baked_breads/${id}/edit`} text="編集する" />
          </div>
        </Detail>
      ) : (
        <List>
          <h3 className="bread-name">{breadName}</h3>
          <div className="action">
            <LinkButton isDisabled={false} url={`/baked_breads/${id}/edit`} text="編集" />
          </div>
        </List>
      )}
    </Container>
  );
});

BakedBreadListItem.defaultProps = {
  listType: 'detail',
};

type ContainerProps = {
  isDetail: boolean;
};

const Container = styled.li<ContainerProps>`
  &:not(:first-child) {
    margin-top: ${({ isDetail }) => `${isDetail ? 8 : 2}px;`};
  }
`;

const Detail = styled.div`
  padding: 20px 16px;
  background: ${StyleConfig.COLOR.WHITE};

  .bread {
    display: flex;
    align-items: center;

    .bread-thumbnail {
      width: 84px;
    }

    .bread-description {
      margin-left: 12px;

      .bread-name {
        font-size: 16px;
        line-height: ${20 / 16};
        color: ${StyleConfig.COLOR.TEXT_COLOR};
      }

      .bread-shop {
        margin-top: 8px;
        font-size: 12px;
        font-weight: bold;
      }

      .bread-count {
        font-size: 12px;
        color: ${StyleConfig.COLOR.TEXT_COLOR};
        span {
          margin-top: 4px;
          color: ${StyleConfig.COLOR.PRIMARY};
          font-weight: bold;
        }
      }

      .bread-timestamp {
        font-size: 12px;
        span {
          font-weight: bold;
        }
      }
    }
  }

  .action {
    margin-top: 12px;
  }
`;

const List = styled.div`
  padding: 8px 12px;
  background: ${StyleConfig.COLOR.WHITE};
  display: flex;
  justify-content: center;
  align-items: center;
  &:not(:first-child) {
    margin-top: 2px;
  }

  .bread-name {
    flex-grow: 2;
    padding-right: 20px;
    font-size: 12px;
  }

  .action {
    text-align: center;
    a {
      padding: 3px 6px;
      width: 50px;
      height: 100%;
    }
  }
`;

export default BakedBreadListItem;
